import React, { useContext } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  LinearProgress,
  Stack
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import urlcat from "urlcat";
import {
  SetPasswordFormData,
  SetPasswordRequest,
  SetPasswordresponse
} from "../../Graphql/types/employee";
import { setEmployeePasswordQuery } from "../../Graphql/querries/employeeQuery";
import { LOGIN } from "../Routes";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import { fetchGraphQL } from "../../Graphql/utils";
import useQueryParams from "../../Hooks/useQueryParams";

const SetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { setAlert } = useContext(AlertBannerContext);
  const { employeeId } = useParams<{ employeeId: string }>();
  const { redirectPage } = useQueryParams();

  const { mutate, isPending } = useMutation<
    SetPasswordresponse,
    Error,
    SetPasswordRequest
  >({
    mutationKey: ["(addEmployee"],
    mutationFn: (input) =>
      fetchGraphQL(setEmployeePasswordQuery, { input }, setAlert),
    onSuccess: () => {
      // navigate to employee detail upon creation
      navigate(urlcat(LOGIN, { redirectPage }));
    }
  });

  const handleSubmitForm = (formData: SetPasswordFormData): void => {
    const { email, password } = formData;
    mutate({ employeeId: employeeId || "", email, password });
  };

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required"),
    password: yup
      .string()
      .min(6, "Password must be at least 6 characters long")
      .matches(/[0-9]/, "Password must contain a number")
      .matches(/[a-zA-Z]/, "Password must contain a letter")
      .matches(
        /[!#$%^&*@#$%^&*()_+.]/,
        "Password must contain a special character. (Any of !#$%^&*@#$%^&*()_+. is accepted)"
      )
      .required("Password is required"),
    confirmPassword: yup
      .string()
      .matches(
        /[!#$%^&*@#$%^&*()_+.]/,
        "Password must contain a special character. (Any of !#$%^&*@#$%^&*()_+. is accepted)"
      )
      .oneOf([yup.ref("password"), undefined], "Passwords must match")
      .required("Confirm Password is required")
  });
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<SetPasswordFormData>({
    resolver: yupResolver(validationSchema)
  });

  return (
    <Container maxWidth="xs">
      <Box textAlign="center" mt={5}>
        <Typography variant="h5" gutterBottom>
          Set Password
        </Typography>
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                fullWidth
                margin="normal"
                variant="outlined"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                variant="outlined"
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm Password"
                type="password"
                fullWidth
                margin="normal"
                variant="outlined"
                error={Boolean(errors.confirmPassword)}
                helperText={errors.confirmPassword?.message}
              />
            )}
          />
          {isPending && (
            <Stack width="100%" paddingBottom="1em" alignSelf="center">
              <LinearProgress />
            </Stack>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            Set Password
          </Button>
        </form>
      </Box>
    </Container>
  );
};

export default SetPassword;
